import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { Dialog } from '@/shared/ui/Dialog';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ErrorMessage } from '@/shared/ui/ErrorMessage';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Spinner } from '@/shared/ui/Spinner';
import { TBody, THead, Table, Td, Th, Tr } from '@/shared/ui/Table';
import { PlusIcon, SearchIcon, UploadIcon, UsersIcon } from '@/shared/ui/icons';

import { EmployeeDialog, PasswordOnce } from './EmployeeDialogs';
import { PayrollTabs } from './PayrollTabs';
import {
  EMPLOYEES_ACCEPT,
  type PayrollEmployee,
  type PayrollEmployeeCreated,
  type PayrollImportResult,
  people,
} from './types';
import { useEmployees, useImportEmployees } from './usePayroll';

const HELP = [
  <>
    La lista de quien recibe nómina: su <strong>nombre</strong> como sale en la nómina, su{' '}
    <strong>NIF o NIE</strong> y el <strong>correo</strong> adonde le llega la suya.
  </>,
  <>
    Cada persona tiene una <strong>contraseña</strong> para abrir el PDF de su nómina. La hace la
    aplicación y la enseña <strong>una sola vez</strong>: apúntala y dásela. Si la pierde, se le
    hace una nueva.
  </>,
  <>
    <strong>Cargar la lista</strong> lee un Excel o un CSV con una fila de títulos (Nombre, NIF,
    Correo). Quien ya está se actualiza; quien es nuevo sale con su contraseña.
  </>,
  <>
    Cambiar el correo o el NIF de alguien, o cargar la lista, te pide tu contraseña: decide adónde
    va cada nómina.
  </>,
];

/**
 * «Trabajadores» (contract v10.53): who receives a payslip, with the NIF that tells whose
 * each page is and the address it goes to. The password is never shown here: only once,
 * when the platform makes it (`PasswordOnce`, and the list of new ones after a load).
 */
/**
 * «Añadir trabajador» opened with a NIF already written comes in the router's state
 * (`navigate('/nominas/trabajadores', { state: { nif } })`), never in the address: a NIF in
 * the URL stays in the history, in the server's logs and in an error report.
 */
function nifOf(state: unknown): string | null {
  if (typeof state !== 'object' || state === null || !('nif' in state)) return null;
  return typeof state.nif === 'string' && state.nif ? state.nif : null;
}

export function EmployeesPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [inactive, setInactive] = useState(false);
  const [editing, setEditing] = useState<PayrollEmployee | null>(null);
  const prefillNif = nifOf(location.state);
  const [adding, setAdding] = useState(prefillNif !== null);
  const [created, setCreated] = useState<PayrollEmployeeCreated | null>(null);
  const [imported, setImported] = useState<PayrollImportResult | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const importList = useImportEmployees();

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const employees = useEmployees({
    ...(debounced ? { q: debounced } : {}),
    ...(inactive ? { include_inactive: true } : {}),
  });
  const items = employees.data?.items ?? [];
  const filtered = Boolean(debounced) || inactive;

  function closeAdding() {
    setAdding(false);
    // Out of the history entry too, or a reload would open it again.
    if (prefillNif !== null) {
      void navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: null },
      );
    }
  }

  const actions = (
    <>
      <input
        ref={input}
        type="file"
        accept={EMPLOYEES_ACCEPT}
        hidden
        aria-label="Fichero de la lista de trabajadores"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          importList.mutate(file, { onSuccess: setImported });
        }}
      />
      <Button
        variant="secondary"
        icon={<UploadIcon size={16} />}
        disabled={importList.isPending}
        onClick={() => input.current?.click()}
      >
        {importList.isPending ? 'Cargando…' : 'Cargar la lista'}
      </Button>
      <Button icon={<PlusIcon size={16} />} onClick={() => setAdding(true)}>
        Añadir trabajador
      </Button>
    </>
  );

  return (
    <>
      <PageHeader title="Nóminas" help={HELP} actions={actions} />
      <PayrollTabs />

      {importList.isError ? (
        <ErrorMessage error={importList.error}>No se pudo cargar la lista</ErrorMessage>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-sm">
          <SearchIcon
            size={16}
            className="text-ink-4 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
          />
          <input
            type="search"
            className="field w-full pl-9"
            placeholder="Buscar por nombre, NIF o correo"
            aria-label="Buscar trabajadores"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <label className="text-ink-2 flex items-center gap-2 text-[13.5px]">
          <input
            type="checkbox"
            className="accent-accent size-4"
            checked={inactive}
            onChange={(event) => setInactive(event.target.checked)}
          />
          Incluir los dados de baja
        </label>
      </div>

      {employees.isPending ? <Spinner variant="block" label="Cargando los trabajadores" /> : null}
      {employees.isError ? (
        <ErrorMessage error={employees.error}>No se pudo cargar la lista</ErrorMessage>
      ) : null}

      {employees.data && items.length === 0 ? (
        filtered ? (
          <EmptyState title="Nadie coincide" hint="Prueba con otra parte del nombre o del NIF." />
        ) : (
          <EmptyState
            icon={<UsersIcon size={24} />}
            title="Todavía no hay nadie en la lista"
            hint="Añade a cada persona o carga la lista de la asesoría en Excel o CSV."
          />
        )
      ) : null}

      {items.length > 0 ? (
        <Card padded={false}>
          <Table label="Trabajadores" minWidth="0">
            <THead>
              <tr>
                <Th>Nombre</Th>
                <Th className="w-32">NIF</Th>
                <Th>Correo</Th>
                <Th className="hidden w-36 md:table-cell">Contraseña</Th>
              </tr>
            </THead>
            <TBody>
              {items.map((employee) => (
                <Tr key={employee.id} interactive onClick={() => setEditing(employee)}>
                  <Td className="text-ink font-medium">
                    {employee.full_name}
                    {employee.active ? null : (
                      <Badge tone="neutral" className="ml-2">
                        De baja
                      </Badge>
                    )}
                  </Td>
                  <Td className="text-ink-2 tabular-nums">
                    {employee.nif ?? <span className="text-ink-4">—</span>}
                  </Td>
                  <Td className="text-ink-2 break-all">
                    {employee.email ?? <span className="text-warn">Sin correo</span>}
                  </Td>
                  <Td className="hidden md:table-cell">
                    {employee.has_password ? (
                      <span className="text-ink-3">Tiene</span>
                    ) : (
                      <span className="text-warn">Sin contraseña</span>
                    )}
                  </Td>
                </Tr>
              ))}
            </TBody>
          </Table>
          {employees.data && employees.data.total > items.length ? (
            <p className="text-ink-3 px-4 py-3 text-[13px]">
              Salen {items.length} de {employees.data.total}: busca para encontrar al resto.
            </p>
          ) : null}
        </Card>
      ) : null}

      {adding ? (
        <EmployeeDialog
          {...(prefillNif ? { initialNif: prefillNif } : {})}
          onClose={closeAdding}
          onCreated={(result) => {
            closeAdding();
            setCreated(result);
          }}
        />
      ) : null}
      {editing ? <EmployeeDialog employee={editing} onClose={() => setEditing(null)} /> : null}
      {created ? (
        <PasswordOnce
          name={created.employee.full_name}
          password={created.password}
          onClose={() => setCreated(null)}
        />
      ) : null}
      {importList.isPending ? (
        // The passwords of whoever is new come in this answer only: the screen stays put
        // until it comes (the answer of `mutate` is lost if the screen goes away).
        <Dialog title="Cargando la lista" size="sm" busy onClose={() => undefined}>
          <Spinner label="Leyendo el fichero…" />
          <p className="text-ink-2 text-[13.5px]">
            Al terminar salen aquí las contraseñas de quien es nuevo. No cierres esta pestaña.
          </p>
        </Dialog>
      ) : null}
      {imported ? <ImportResult result={imported} onClose={() => setImported(null)} /> : null}
    </>
  );
}

/**
 * What the load did, and the passwords of whoever is new: shown this once, like one by
 * one, with a button that copies them all.
 */
function ImportResult({ result, onClose }: { result: PayrollImportResult; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const passwords = result.passwords ?? [];
  const skipped = result.skipped ?? [];
  const counts = [
    result.created ? `${people(result.created)} de alta` : null,
    result.updated ? `${people(result.updated)} con datos cambiados` : null,
    result.unchanged ? `${people(result.unchanged)} igual que estaba` : null,
  ].filter(Boolean);

  async function copyAll() {
    const text = passwords.map((each) => `${each.full_name}\t${each.password}`).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  }

  return (
    <Dialog
      title="Lista cargada"
      description={counts.length > 0 ? counts.join(' · ') : 'No ha cambiado nada.'}
      onClose={onClose}
      // With passwords, only «Ya las he apuntado» closes it: Escape, «×» or a click outside
      // would lose them for good.
      busy={passwords.length > 0}
      footer={
        <>
          {passwords.length > 0 ? (
            <Button variant="secondary" onClick={() => void copyAll()}>
              {copied ? 'Copiadas' : 'Copiar todas'}
            </Button>
          ) : null}
          <Button onClick={onClose} data-autofocus>
            {passwords.length > 0 ? 'Ya las he apuntado' : 'Cerrar'}
          </Button>
        </>
      }
    >
      {passwords.length > 0 ? (
        <div className="flex flex-col gap-2">
          <p className="text-ink-2 text-[13.5px]">
            Las contraseñas de quien es nuevo. Dale a cada uno la suya.{' '}
            <strong>No se vuelven a enseñar.</strong>
          </p>
          <ul className="border-line divide-line-2 divide-y rounded-md border">
            {passwords.map((each) => (
              <li
                key={each.employee_id}
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
              >
                <span className="text-ink text-[14px]">{each.full_name}</span>
                <span className="text-ink font-mono text-[15px] select-all">{each.password}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {skipped.length > 0 ? (
        <div className="mt-3 flex flex-col gap-1">
          <p className="text-warn text-[13.5px] font-medium">
            {skipped.length === 1
              ? 'Una fila no se ha cargado'
              : `${skipped.length} filas no se han cargado`}
          </p>
          <ul className="text-ink-2 text-[13px]">
            {skipped.map((each) => (
              <li key={`${each.row}-${each.reason}`}>
                Fila {each.row}: {each.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Dialog>
  );
}
